import { useCallback, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { cld, cldSrcSet } from "../lib/api";

export const Lightbox = ({ photos, index, onClose, onIndex, label = "Photo" }) => {
  const open = index !== null && index >= 0 && photos[index];

  const step = useCallback(
    (dir) => onIndex((index + dir + photos.length) % photos.length),
    [index, photos.length, onIndex],
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, step]);

  if (!open) return null;
  const photo = photos[index];

  return (
    <div className="lb" role="dialog" aria-modal="true" onClick={onClose} data-testid="lightbox">
      <button className="lb-close" onClick={onClose} aria-label="Close" data-testid="lightbox-close">
        <X size={20} strokeWidth={2.5} />
      </button>

      {photos.length > 1 && (
        <button
          className="lb-nav lb-prev"
          onClick={(e) => {
            e.stopPropagation();
            step(-1);
          }}
          aria-label="Previous photo"
          data-testid="lightbox-prev"
        >
          <ChevronLeft size={26} strokeWidth={2.5} />
        </button>
      )}

      <figure className="lb-stage" onClick={(e) => e.stopPropagation()}>
        <img
          src={cld(photo.url, 1400)}
          srcSet={cldSrcSet(photo.url, [900, 1400, 1800])}
          sizes="(max-width: 768px) 100vw, 1400px"
          alt={`${label} ${index + 1}`}
          data-testid="lightbox-image"
        />
        <figcaption className="mono lb-count" data-testid="lightbox-counter">
          {index + 1} / {photos.length}
        </figcaption>
      </figure>

      {photos.length > 1 && (
        <button
          className="lb-nav lb-next"
          onClick={(e) => {
            e.stopPropagation();
            step(1);
          }}
          aria-label="Next photo"
          data-testid="lightbox-next"
        >
          <ChevronRight size={26} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
};
